import type {
  PiMatrixAgentAgentPromptResult,
  PiMatrixAgentAgentReport,
  PiMatrixAgentAgentSession,
  PiMatrixAgentAgentStreamEvent,
  PiMatrixAgentChangeSet,
  PiMatrixAgentModelOption,
  PiMatrixAgentOAuthLoginAttempt,
  PiMatrixAgentOAuthProvider,
  PiMatrixAgentPatch,
  PiMatrixAgentProposal,
  PiMatrixAgentSnapshot,
  PiMatrixAgentThinkingLevel,
  PiMatrixAgentToolResponse,
  PiStructuredGenerationExecution,
  PiStructuredGenerationRequest
} from '../types/piMatrixAgent'

const isDevelopment = process.env.NODE_ENV === 'development'

export const PI_MATRIX_AGENT_BASE_URL = process.env.REACT_APP_PI_MATRIX_AGENT_URL ||
  (isDevelopment ? 'http://127.0.0.1:4317' : '')

export const PI_MATRIX_AGENT_ENDPOINTS = {
  structuredGenerations: `${PI_MATRIX_AGENT_BASE_URL}/structured-generations`,
  createSession: `${PI_MATRIX_AGENT_BASE_URL}/sessions`,
  updateSnapshot: (sessionId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/snapshot`,
  invokeTool: (sessionId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/tool`,
  applyPreview: (sessionId: string, previewId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/previews/${previewId}/apply`,
  getProposal: (sessionId: string, proposalId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/proposals/${proposalId}`,
  getChangeSet: (sessionId: string, changeSetId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/change-sets/${changeSetId}`,
  prepareChangeSet: (sessionId: string, changeSetId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/change-sets/${changeSetId}/prepare`,
  createAgent: (sessionId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/agent`,
  abortAgent: (sessionId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/agent/abort`,
  promptAgent: (sessionId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/agent/prompt`,
  promptAgentStream: (sessionId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/agent/prompt/stream`,
  agentReport: (sessionId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/agent/report`,
  listModels: (provider?: string) => `${PI_MATRIX_AGENT_BASE_URL}/models${provider ? `?provider=${encodeURIComponent(provider)}` : ''}`,
  listOAuthProviders: `${PI_MATRIX_AGENT_BASE_URL}/oauth/providers`,
  startOAuthLogin: (sessionId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/auth/login`,
  getOAuthLoginStatus: (sessionId: string, attemptId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/auth/login/${attemptId}`,
  submitOAuthLoginInput: (sessionId: string, attemptId: string) => `${PI_MATRIX_AGENT_BASE_URL}/sessions/${sessionId}/auth/login/${attemptId}/input`
} as const

const fetchPiMatrixAgent = async (url: string, options: RequestInit): Promise<Response> => {
  try {
    return await fetch(url, options)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    const endpoint = PI_MATRIX_AGENT_BASE_URL || 'the configured same-origin endpoint'
    throw new Error(`Pi service unavailable at ${endpoint}. Restart Variorum so its Pi sidecar can start, then try again.`)
  }
}

async function requestJson<T>(url: string, options: RequestInit): Promise<T> {
  const response = await fetchPiMatrixAgent(url, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    ...options
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(payload?.error || payload?.message || `Request failed: ${response.status}`)
  }
  return payload as T
}

export const requestPiStructuredGeneration = async (
  request: PiStructuredGenerationRequest,
  signal?: AbortSignal
) => requestJson<PiStructuredGenerationExecution>(
  PI_MATRIX_AGENT_ENDPOINTS.structuredGenerations,
  {
    method: 'POST',
    body: JSON.stringify(request),
    signal
  }
)

export const createPiMatrixAgentSession = async ({
  snapshot
}: {
  snapshot: PiMatrixAgentSnapshot
}) => requestJson<{
  sessionId: string
  snapshotId: string
  tools: Array<{ name: string; label: string; description: string }>
}>(
  PI_MATRIX_AGENT_ENDPOINTS.createSession,
  {
    method: 'POST',
    body: JSON.stringify({ snapshot })
  }
)

export const syncPiMatrixAgentSnapshot = async ({
  sessionId,
  snapshot
}: {
  sessionId: string
  snapshot: PiMatrixAgentSnapshot
}) => requestJson<{
  sessionId: string
  snapshotId: string
  agentRestartRequired: boolean
}>(
  PI_MATRIX_AGENT_ENDPOINTS.updateSnapshot(sessionId),
  {
    method: 'POST',
    body: JSON.stringify({ snapshot })
  }
)

export const invokePiMatrixAgentTool = async ({
  sessionId,
  toolName,
  arguments: args
}: {
  sessionId: string
  toolName: string
  arguments: Record<string, unknown>
}) => requestJson<PiMatrixAgentToolResponse>(
  PI_MATRIX_AGENT_ENDPOINTS.invokeTool(sessionId),
  {
    method: 'POST',
    body: JSON.stringify({
      toolName,
      arguments: args
    })
  }
)

export const applyPiMatrixAgentPreview = async ({
  sessionId,
  previewId
}: {
  sessionId: string
  previewId: string
}) => requestJson<PiMatrixAgentPatch>(
  PI_MATRIX_AGENT_ENDPOINTS.applyPreview(sessionId, previewId),
  {
    method: 'POST',
    body: JSON.stringify({})
  }
)

export const getPiMatrixAgentProposal = async ({
  sessionId,
  proposalId
}: {
  sessionId: string
  proposalId: string
}) => requestJson<PiMatrixAgentProposal>(
  PI_MATRIX_AGENT_ENDPOINTS.getProposal(sessionId, proposalId),
  { method: 'GET' }
)

export const getPiMatrixAgentChangeSet = async ({
  sessionId,
  changeSetId
}: {
  sessionId: string
  changeSetId: string
}) => requestJson<PiMatrixAgentChangeSet>(
  PI_MATRIX_AGENT_ENDPOINTS.getChangeSet(sessionId, changeSetId),
  { method: 'GET' }
)

export const preparePiMatrixAgentChangeSet = async ({
  sessionId,
  changeSetId
}: {
  sessionId: string
  changeSetId: string
}) => requestJson<PiMatrixAgentPatch>(
  PI_MATRIX_AGENT_ENDPOINTS.prepareChangeSet(sessionId, changeSetId),
  { method: 'POST', body: JSON.stringify({}) }
)

export const createPiMatrixAgentAgentSession = async ({
  sessionId,
  cwd,
  model,
  thinkingLevel,
  resumeAgentSessionId,
  sessionName,
  tools
}: {
  sessionId: string
  cwd?: string
  model?: {
    provider: string
    modelId: string
  }
  thinkingLevel?: PiMatrixAgentThinkingLevel
  resumeAgentSessionId?: string
  sessionName?: string
  tools?: string[]
}) => requestJson<PiMatrixAgentAgentSession>(
  PI_MATRIX_AGENT_ENDPOINTS.createAgent(sessionId),
  {
    method: 'POST',
    body: JSON.stringify({
      cwd,
      model,
      thinkingLevel,
      resumeAgentSessionId,
      sessionName,
      tools
    })
  }
)

export const listPiMatrixAgentModels = async ({
  provider
}: {
  provider?: string
} = {}) => requestJson<{
  models: PiMatrixAgentModelOption[]
}>(
  PI_MATRIX_AGENT_ENDPOINTS.listModels(provider),
  { method: 'GET' }
)

export const getPiMatrixAgentAgentReport = async ({
  sessionId
}: {
  sessionId: string
}) => requestJson<PiMatrixAgentAgentReport>(
  PI_MATRIX_AGENT_ENDPOINTS.agentReport(sessionId),
  { method: 'GET' }
)

export const promptPiMatrixAgentAgent = async ({
  sessionId,
  prompt,
  streamingBehavior,
  inquiryMode
}: {
  sessionId: string
  prompt: string
  streamingBehavior?: 'steer' | 'followUp'
  inquiryMode?: boolean
}) => requestJson<PiMatrixAgentAgentPromptResult>(
  PI_MATRIX_AGENT_ENDPOINTS.promptAgent(sessionId),
  {
    method: 'POST',
    body: JSON.stringify({
      prompt,
      streamingBehavior,
      inquiryMode
    })
  }
)

export const abortPiMatrixAgentAgent = async ({
  sessionId
}: {
  sessionId: string
}) => requestJson<{
  sessionId: string
  aborted: boolean
}>(
  PI_MATRIX_AGENT_ENDPOINTS.abortAgent(sessionId),
  {
    method: 'POST',
    body: JSON.stringify({})
  }
)

export const promptPiMatrixAgentAgentStream = async ({
  sessionId,
  prompt,
  streamingBehavior,
  inquiryMode,
  signal,
  onEvent,
}: {
  sessionId: string
  prompt: string
  streamingBehavior?: 'steer' | 'followUp'
  inquiryMode?: boolean
  signal?: AbortSignal
  onEvent: (event: PiMatrixAgentAgentStreamEvent) => void
}): Promise<void> => {
  const response = await fetchPiMatrixAgent(PI_MATRIX_AGENT_ENDPOINTS.promptAgentStream(sessionId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
    },
    body: JSON.stringify({ prompt, streamingBehavior, inquiryMode }),
    signal,
  })
  if (!response.ok) {
    const payload = await response.json().catch(() => null)
    throw new Error(payload?.error || payload?.message || `Request failed: ${response.status}`)
  }
  if (!response.body) {
    throw new Error('Pi sidecar did not provide a streaming response body')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  const consumeLine = (line: string) => {
    if (!line.trim()) return
    const event = JSON.parse(line) as PiMatrixAgentAgentStreamEvent
    if (event.type === 'stream.failed') throw new Error(event.message)
    onEvent(event)
  }

  while (true) {
    const { done, value } = await reader.read()
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''
    lines.forEach(consumeLine)
    if (done) break
  }
  consumeLine(buffer)
}

export const listPiMatrixAgentOAuthProviders = async () => requestJson<{
  providers: PiMatrixAgentOAuthProvider[]
}>(
  PI_MATRIX_AGENT_ENDPOINTS.listOAuthProviders,
  {
    method: 'GET'
  }
)

export const startPiMatrixAgentOAuthLogin = async ({
  sessionId,
  providerId,
  cwd,
  model,
  thinkingLevel,
  sessionName,
  tools,
  autoStartAgent
}: {
  sessionId: string
  providerId: string
  cwd?: string
  model?: {
    provider: string
    modelId?: string
  }
  thinkingLevel?: PiMatrixAgentThinkingLevel
  sessionName?: string
  tools?: string[]
  autoStartAgent?: boolean
}) => requestJson<PiMatrixAgentOAuthLoginAttempt>(
  PI_MATRIX_AGENT_ENDPOINTS.startOAuthLogin(sessionId),
  {
    method: 'POST',
    body: JSON.stringify({
      providerId,
      cwd,
      model,
      thinkingLevel,
      sessionName,
      tools,
      autoStartAgent
    })
  }
)

export const getPiMatrixAgentOAuthLoginStatus = async ({
  sessionId,
  attemptId
}: {
  sessionId: string
  attemptId: string
}) => requestJson<PiMatrixAgentOAuthLoginAttempt>(
  PI_MATRIX_AGENT_ENDPOINTS.getOAuthLoginStatus(sessionId, attemptId),
  {
    method: 'GET'
  }
)

export const submitPiMatrixAgentOAuthLoginInput = async ({
  sessionId,
  attemptId,
  value
}: {
  sessionId: string
  attemptId: string
  value: string
}) => requestJson<PiMatrixAgentOAuthLoginAttempt>(
  PI_MATRIX_AGENT_ENDPOINTS.submitOAuthLoginInput(sessionId, attemptId),
  {
    method: 'POST',
    body: JSON.stringify({ value })
  }
)
